/**
 * パスワードポリシー（Cognito User Pool の password_policy と同じ内容）。
 *
 * kuden-infra/modules/cognito-user-pool の設定:
 *   minimum_length=8 / require_uppercase / require_lowercase / require_symbols
 *   （require_numbers は false）
 *
 * 記号の集合は Cognito が「記号」として認める文字と一致させる。
 * https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
 * 画面側の判定が Cognito より狭いと、Cognito では通るパスワードを画面で弾いてしまい、
 * 逆に広いと Cognito の InvalidPasswordException になるまで気付けない。
 */
export const PASSWORD_MIN_LENGTH = 8;

/** Cognito が記号として扱う文字（半角スペースは先頭・末尾に使えないため一覧から除外）。 */
export const PASSWORD_ALLOWED_SYMBOLS = '^$*.[]{}()?-"!@#%&/\\,><\':;|_~`+=';

const escapeForCharClass = (chars: string) => chars.replace(/[\\\]^-]/g, '\\$&');

const SYMBOL_PATTERN = new RegExp(`[${escapeForCharClass(PASSWORD_ALLOWED_SYMBOLS)}]`);

/** 画面の注意書きに表示する記号一覧（読みやすいよう半角スペース区切り）。 */
export const PASSWORD_ALLOWED_SYMBOLS_DISPLAY = PASSWORD_ALLOWED_SYMBOLS.split('').join(' ');

export const PASSWORD_POLICY_MESSAGES = {
  minLength: `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください。`,
  charTypes: '１文字以上の英大小文字、記号を使用してください。',
} as const;

export type PasswordPolicyCheck = {
  minLength: boolean;
  upperCase: boolean;
  lowerCase: boolean;
  symbol: boolean;
};

/** ポリシーの各項目を満たしているかを個別に返す（画面のチェックリスト表示用）。 */
export function checkPasswordPolicy(password: string): PasswordPolicyCheck {
  return {
    minLength: password.length >= PASSWORD_MIN_LENGTH,
    upperCase: /[A-Z]/.test(password),
    lowerCase: /[a-z]/.test(password),
    symbol: SYMBOL_PATTERN.test(password),
  };
}

/**
 * パスワードがポリシーを満たしているかを判定し、満たさない項目のメッセージを返す。
 * 空配列なら合格。
 */
export function validatePassword(password: string): string[] {
  const check = checkPasswordPolicy(password);
  const errors: string[] = [];

  if (!check.minLength) {
    errors.push(PASSWORD_POLICY_MESSAGES.minLength);
  }

  if (!check.upperCase || !check.lowerCase || !check.symbol) {
    errors.push(PASSWORD_POLICY_MESSAGES.charTypes);
  }

  return errors;
}
